import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useState } from "react";
import { useAppSelector } from "@/redux/hooks";
import { profileSelector } from "@/redux/slices/profile/selectors";

// Type definitions for Kinde claims
type UserProperty = { v: string };
type ClaimsWithUserProperties = {
  user_properties?: {
    kp_usr_utm_content?: UserProperty;
  };
};

type WelcomeOverlayUserType = "operator" | "client" | "invite";

/**
 * Hook to manage welcome overlay state for first-time registrations
 *
 * Checks sessionStorage for "justCompletedOnboarding" flag and determines
 * user type from profile metadata or Kinde claims to show appropriate welcome message.
 *
 * @returns Object containing showWelcome state, userType, and handleClose function
 */
export const useWelcomeOverlay = () => {
  const { getClaims } = useKindeAuth();
  const profile = useAppSelector(profileSelector);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userType, setUserType] = useState<WelcomeOverlayUserType | null>(null);

  useEffect(() => {
    // Check if user just completed onboarding
    const justCompletedOnboarding = sessionStorage.getItem("justCompletedOnboarding") === "true";

    if (!justCompletedOnboarding) return;

    // Clear the flag immediately to prevent showing on refresh
    sessionStorage.removeItem("justCompletedOnboarding");

    // Determine user type from profile metadata or Kinde claims
    const accountType = profile?.accountMetadata?.type;

    if (accountType) {
      // Use account metadata type (most reliable)
      const mappedType = mapAccountTypeToWelcomeType(accountType);
      if (mappedType) {
        setUserType(mappedType);
        setShowWelcome(true);
      }
    } else {
      // Fallback to Kinde claims
      getClaims("idToken")
        .then((claims) => {
          const typedClaims = claims as ClaimsWithUserProperties;
          const utmContent = typedClaims?.user_properties?.kp_usr_utm_content?.v;

          const mappedType = mapAccountTypeToWelcomeType(utmContent);
          if (mappedType) {
            setUserType(mappedType);
            setShowWelcome(true);
          }
        })
        .catch(() => {
          console.error("Failed to fetch Kinde claims for welcome overlay");
        });
    }
  }, [getClaims, profile]);

  const handleClose = () => {
    setShowWelcome(false);
    setUserType(null);
  };

  return { showWelcome, userType, handleClose };
};

/**
 * Maps account type from profile/Kinde claims to welcome overlay type
 * @param type - Account type from profile.accountMetadata.type or Kinde utm_content
 * @returns Mapped welcome overlay type or null if invalid
 */
const mapAccountTypeToWelcomeType = (type?: string): WelcomeOverlayUserType | null => {
  if (!type) return null;

  switch (type) {
    case "operator":
      return "operator";
    case "passenger":
    case "executive_assistant":
    case "client":
      return "client";
    case "user_account":
      return "invite";
    default:
      return null;
  }
};
